/* The Turn Tracker module: everything that used to be the whole app.
 *
 * Two components register with the console — the page that fills the body, and
 * the nav that sits in the top bar next to the module switcher. Its modals,
 * hotkeys and day-roll notice live in here rather than in the shell, so the
 * shell stays free of anything that knows what a turn is.
 */
import * as Vue from 'vue';
import { apps } from '../apps.js';
import { store } from '../store.js';
import { AddStaff } from './add-staff.js';
import { AppointmentsPane } from './appointments-pane.js';
import { ApptGhost } from './appt-drag.js';
import { ApptMenu } from './appt-menu.js';
import { AssignTurn } from './assign-turn.js';
import { AttachAppointment } from './attach-appointment.js';
import { BookClash } from './book-clash.js';
import { OrderAsk } from './order-ask.js';
import { AutoStartAsk } from './auto-start-ask.js';
import { CellMenu } from './cell-menu.js';
import { CheckinMissed } from './checkin-missed.js';
import { DeskNotes } from './desk-notes.js';
import { SkipRemove } from './skip-remove.js';
import { EndDay } from './end-day.js';
import { HistoryView } from './history-view.js';
import { MissedWalkin } from './missed-walkin.js';
import { MoveConfirm } from './move-confirm.js';
import { PhoneBar, PhoneStrips } from './phone-bar.js';
import { PluginConfirm } from './plugin-confirm.js';
import { ServiceBar } from './service-bar.js';
import { ServiceSheet } from './service-sheet.js';
import { ServiceWarning } from './service-warning.js';
import { Settings } from './settings.js';
import { StaffMenu } from './staff-menu.js';
import { StartTurn } from './start-turn.js';
import { SwapConfirm } from './swap-confirm.js';
import { DropChoice } from './drop-choice.js';
import { TurnBoard } from './turn-board.js';
import { TurnList } from './turn-list.js';
import { TurnMenu } from './turn-menu.js';

/* ---------------- top-bar nav + stats ---------------- */

var TurnTrackerNav = {
  name: 'TurnTrackerNav',
  setup: function () {
    var totalTurns = Vue.computed(function () { return store.state.turns.length; });
    var running = Vue.computed(function () {
      return store.state.turns.filter(function (t) { return t.endedAt == null; }).length;
    });
    return {
      ui: store.ui,
      totalTurns: totalTurns,
      running: running,
      presentStaff: store.presentStaff,
      /* The fourth stat, and the only one that is not there all day — see
       * `lastFinish` in the store for the two hours and for what it counts.
       * Null most of the day, and null is how it stays out of the bar. */
      lastFinish: store.lastFinish,
      lastFinishNote: store.lastFinishNote,
      closed: store.dayClosed,
      openEndDay: store.openEndDay,
      // Closed, the button is the way back to the day's numbers rather than a
      // second chance to close it.
      endLabel: Vue.computed(function () {
        return store.dayClosed.value ? 'Day closed' : 'End day';
      }),
      endTitle: Vue.computed(function () {
        return store.dayClosed.value
          ? 'The book is shut for today — the day’s numbers, and the way to reopen it'
          : 'Stop everything still running, shut the board to new turns, and read the day';
      })
    };
  },
  template: [
    '<div v-if="!ui.phone" class="topbar__stats">',
    '  <span><strong>{{ presentStaff.length }}</strong> in today</span>',
    '  <span><strong>{{ totalTurns }}</strong> services</span>',
    '  <span><strong>{{ running }}</strong> running</span>',
    /* The end of the day, for the last two hours of it. Last in the run because
       it is the one that comes and goes: three counts that shuffle along to
       make room for a fourth would be read as the bar having changed, and it
       has not. Marked when it lands past closing, which is the reading somebody
       is looking for when they check it at all. */
    '  <span v-if="lastFinish" class="topbar__last"',
    '    :class="{ \'is-late\': lastFinish.over > 0 }"',
    '    :title="lastFinishNote(lastFinish)">',
    /* Value over label, like the three counts beside it: the bar has one shape
       and a fourth entry that inverted it would read as a different kind of
       thing. "Done" rather than "finishes" because it is read across a room in
       the corner of an eye and the shortest true word wins, and the ~ is the
       whole disclaimer that this is an estimate off the countdowns, in the one
       character there is room for beside a time. */
    '    <strong>~{{ lastFinish.clock }}</strong> done</span>',
    '</div>',
    /* Two of the three on a phone, and the shorter wording. Not a cosmetic
       trim: the three full-length ones are two hundred pixels of a bar that is
       390 wide, and what they were taking it from was the salon's own name,
       which had got as far as "Co...". The one dropped is the day's service
       count — it is the only one of the three that is not also a fact about
       right now, and the board underneath is about right now.
       Titled rather than abbreviated further: "8 in" is readable and
       "8 in today" is what it means. */
    '<div v-else class="topbar__stats topbar__stats--tight">',
    '  <span :title="presentStaff.length + \' technicians in today\'">',
    '    <strong>{{ presentStaff.length }}</strong> in</span>',
    '  <span :title="running + \' services running now, of \' + totalTurns + \' today\'">',
    '    <strong>{{ running }}</strong> running</span>',
    '</div>',
    /* Not on a phone: these four are the bottom bar there, under the thumb,
       and the top of a phone held one-handed is the part of the screen that
       cannot be reached. The stats above stay — they are read, not pressed. */
    '<nav v-if="!ui.phone" class="topbar__nav">',
    /* Not a tab: it does not change what is on screen, it ends the day. So it
       leads the nav rather than trailing it, which puts it next to the running
       count — the number it is about, and the one somebody checks before
       shutting the book — and leaves the three view tabs as an unbroken run of
       three. Quieter than they are, and marked once the book is shut. */
    '  <button type="button" class="btn btn--end" :class="{ \'is-closed\': closed }"',
    '    @click="openEndDay" :title="endTitle">{{ endLabel }}</button>',
    '  <button type="button" class="btn btn--tab" :class="{ \'is-on\': ui.view === \'board\' }"',
    '    @click="ui.view = \'board\'">Board</button>',
    '  <button type="button" class="btn btn--tab" :class="{ \'is-on\': ui.view === \'history\' }"',
    '    @click="ui.view = \'history\'">History</button>',
    '  <button type="button" class="btn btn--tab" :class="{ \'is-on\': ui.view === \'settings\' }"',
    '    @click="ui.view = \'settings\'" title="Staff, services, appointments, branding and board options">',
    '    <span class="btn__gear" aria-hidden="true">&#9881;</span> Settings</button>',
    '</nav>'
  ].join('\n')
};

/* ---------------- the page ---------------- */

var TurnTracker = {
  name: 'TurnTracker',
  components: {
    DeskNotes: DeskNotes,
    ServiceBar: ServiceBar,
    ServiceSheet: ServiceSheet,
    TurnBoard: TurnBoard,
    TurnList: TurnList,
    PhoneBar: PhoneBar,
    PhoneStrips: PhoneStrips,
    CellMenu: CellMenu,
    Settings: Settings,
    HistoryView: HistoryView,
    MoveConfirm: MoveConfirm,
    SwapConfirm: SwapConfirm,
    DropChoice: DropChoice,
    AssignTurn: AssignTurn,
    ServiceWarning: ServiceWarning,
    AttachAppointment: AttachAppointment,
    StartTurn: StartTurn,
    StaffMenu: StaffMenu,
    ApptMenu: ApptMenu,
    TurnMenu: TurnMenu,
    BookClash: BookClash,
    OrderAsk: OrderAsk,
    AutoStartAsk: AutoStartAsk,
    AddStaff: AddStaff,
    AppointmentsPane: AppointmentsPane,
    EndDay: EndDay,
    PluginConfirm: PluginConfirm,
    MissedWalkin: MissedWalkin,
    CheckinMissed: CheckinMissed,
    SkipRemove: SkipRemove,
    ApptGhost: ApptGhost
  },
  setup: function () {
    // Single-key service hotkeys. Shift+key arms without booking, so a turn
    // can still be placed by hand.
    // The one Escape handler for the module. Every modal used to add its own
    // as well — nine window listeners doing what this already does.
    function onKey(e) {
      if (e.metaKey || e.ctrlKey || e.altKey) return;

      // Escape is handled before the text-field guard below, because that
      // guard exists to stop service hotkeys stealing letters from an input,
      // and Escape is not a letter. Closing a sheet from inside its own text
      // box is exactly when it is wanted.
      if (e.key === 'Escape') {
        // The collision warning opens over the sheet that raised it. Closing
        // both would put the desk further back than they asked to go — they
        // said no to that technician, not to starting a turn at all.
        if (store.ui.bookClash) { store.closeBookClash(); return; }
        // The same for the order question: it opens over the sheet that raised it.
        if (store.ui.orderAsk) { store.closeOrderAsk(); return; }
        // The busy-technician question sits behind the assign sheet it opens;
        // Escape on that sheet closes the sheet, not the question.
        if (store.ui.autoStartAsk && !store.ui.assign) { store.closeAutoStartAsk(); return; }
        // A phone with a keyboard attached is rare and a tablet in a keyboard
        // case is not, and either way Escape means the same thing here as it
        // does to every sheet below.
        store.closePhoneExtras();
        store.closeCellMenu();
        store.closeMoveConfirm();
        store.closeSwapConfirm();
        // Escape is the "no" here in the strong sense: the card came off the
        // board when the sheet opened and nothing was written, so dismissing it
        // leaves the booking exactly where it was.
        store.closeDropChoice();
        store.closeAssign();
        store.closeServiceWarning();
        store.closeAttachPick();
        store.closeEmptyPick();
        store.closeStaffMenu();
        store.closeApptMenu();
        store.closeTurnMenu();
        store.closeBookClash();
        store.closeAddStaff();
        store.closePluginPrompt();
        store.closeMissedWalkin();
        // Escape is the "no" — see js/components/checkin-missed.js for why a
        // question about a number is allowed to be dismissed like one.
        store.closeCheckinMissed();
        store.closeSkipRemove();
        store.closeEndDay();
        store.disarm();
        store.ui.requestMode = false;
        return;
      }

      var tag = e.target && e.target.tagName;
      if (tag && /^(INPUT|TEXTAREA|SELECT)$/.test(tag)) return;
      if (store.ui.view !== 'board' || store.ui.cellMenuTurnId || store.ui.moveConfirm ||
          store.ui.swapConfirm || store.ui.dropChoice ||
          store.ui.assign || store.ui.serviceWarning || store.ui.attachPick ||
          store.ui.apptMenu || store.ui.turnMenu || store.ui.bookClash ||
          store.ui.emptyPick || store.ui.staffMenu || store.ui.addStaff ||
          store.ui.pluginPrompt || store.ui.missedWalkin || store.ui.checkinMissed ||
          store.ui.skipRemove || store.ui.endDay) return;
      if (!e.key || e.key.length !== 1) return;

      // Service shortcuts win; R only toggles request mode when none claims it.
      var key = e.key.toUpperCase();
      if (store.shortcutMap.value[key]) {
        e.preventDefault();
        store.bookByShortcut(key, e.shiftKey);
        return;
      }
      // R used to switch request mode on. Requests come from Mangomint now
      // (Kevin, 2026-10-19), so R is left alone.
    }

    Vue.onMounted(function () {
      window.addEventListener('keydown', onKey);
      store.init();
    });
    Vue.onBeforeUnmount(function () {
      window.removeEventListener('keydown', onKey);
    });

    // Whoever the book has working who is not on the board. Named rather than
    // counted while it is one or two people, because a name is actionable and
    // "3 technicians" is not.
    var missingLabel = Vue.computed(function () {
      var m = store.missingBookedStaff.value;
      if (!m.length) return '';
      var first = m[0];
      var when = store.formatClock(first.startsAt);
      if (m.length === 1) {
        return first.staff.name + ' has a ' + when + ' appointment (' +
          first.clientName + ') but is not on the board.';
      }
      if (m.length === 2) {
        return first.staff.name + ' and ' + m[1].staff.name +
          ' have appointments today but are not on the board. ' +
          first.staff.name + ' is first, at ' + when + '.';
      }
      return m.length + ' technicians have appointments today but are not on the ' +
        'board — ' + first.staff.name + ' first, at ' + when + '.';
    });

    return {
      ui: store.ui,
      state: store.state,
      appts: store.appts,
      missingBooked: store.missingBookedStaff,
      missingLabel: missingLabel,
      addMissingBooked: store.addMissingBooked,
      dismissMissingBooked: store.dismissMissingBooked,
      // Off, there is no pane, no book to assign from and nothing to attach —
      // the whole idea of an appointment leaves with the plugin.
      scheduled: store.schedulingOn,
      // Whether the board is drawn as a list of technicians rather than as the
      // grid. Only ever true on a phone; see store.boardIsList.
      isList: store.boardIsList,
      switchToToday: store.switchToToday,
      todayKey: store.todayKey,
      closed: store.dayClosed,
      openEndDay: store.openEndDay,
      reopenDay: store.reopenDay,
      staleNote: store.staleBoardNote
    };
  },
  template: [
    '<div v-if="ui.dateRolled" class="notice">',
    '  <span>It is now <strong>{{ todayKey() }}</strong> but the board is still showing <strong>{{ state.businessDate }}</strong>.</span>',
    '  <button type="button" class="btn btn--primary btn--sm" @click="switchToToday">Start today</button>',
    '</div>',
    '<div v-if="ui.loadError" class="notice notice--warn"><span>{{ ui.loadError }}</span></div>',
    '<div v-else-if="staleNote" class="notice notice--warn"><span>{{ staleNote }}</span></div>',
    /* The board still shows the whole day — it is shut, not cleared — so it
       has to say so somewhere other than the square somebody is about to
       click. */
    '<div v-if="closed" class="notice notice--closed">',
    '  <span>The book is <strong>closed</strong> for today. Nothing new can go on the board.</span>',
    '  <button type="button" class="btn btn--primary btn--sm" @click="openEndDay">The day</button>',
    '  <button type="button" class="link-btn" @click="reopenDay">Reopen it</button>',
    '</div>',
    /* The book knows somebody is working who the board does not. Non-blocking
       on purpose: it can appear mid-service, and the front desk decides. */
    '<div v-if="scheduled && missingBooked.length" class="notice">',
    '  <span>{{ missingLabel }}</span>',
    '  <button type="button" class="btn btn--primary btn--sm" @click="addMissingBooked">',
    '    {{ missingBooked.length === 1 ? "Add them" : "Add them all" }}</button>',
    '  <button type="button" class="link-btn" @click="dismissMissingBooked">Not today</button>',
    '</div>',
    '',
    '<div class="shell" :class="{ \'shell--pane-open\': scheduled && appts.open }">',
    /* On a phone the book is a screen rather than a pane, so the two swap
       places instead of sharing the width — see phone-bar.js and the `book`
       view, which only a phone ever sets. */
    '  <main v-if="!ui.phone || ui.view !== \'book\'">',
    '    <p v-if="ui.loading" class="config__empty" style="width:90%;margin:2rem auto">Loading the board…</p>',
    '    <template v-else-if="ui.view === \'board\'">',
    /* Notes first: a problem that needs a person belongs where the desk is
       already looking, above both the phone counts and Next availability. */
    '      <desk-notes />',
    /* The service menu is behind the ＋ on a phone; what is left above the board
       is the two counts. See phone-bar.js for why they are counts. */
    '      <phone-strips v-if="ui.phone" />',
    '      <service-bar v-else />',
    /* Same board, read down instead of across, whenever there is not the width
       for columns. store.boardIsList is the whole of that decision. */
    '      <turn-list v-if="isList" />',
    '      <turn-board v-else />',
    '    </template>',
    '    <settings v-else-if="ui.view === \'settings\'" />',
    '    <history-view v-else-if="ui.view === \'history\'" />',
    '  </main>',
    '  <appointments-pane v-if="scheduled && (!ui.phone || ui.view === \'book\')" />',
    '</div>',
    '',
    '<phone-bar v-if="ui.phone" />',
    '<service-sheet v-if="ui.phone && ui.serviceSheet" />',
    '',
    '<cell-menu />',
    '<move-confirm />',
    '<swap-confirm />',
    '<drop-choice />',
    '<service-warning />',
    '<start-turn />',
    '<staff-menu />',
    '<appt-menu />',
    '<turn-menu />',
    '<book-clash />',
    '<order-ask />',
    '<auto-start-ask />',
    '<add-staff />',
    '<plugin-confirm />',
    '<missed-walkin />',
    '<checkin-missed />',
    '<skip-remove />',
    '<end-day />',
    '<appt-ghost />',
    '<template v-if="scheduled">',
    '  <assign-turn />',
    '  <attach-appointment />',
    '</template>'
  ].join('\n')
};

export { TurnTracker };
export { TurnTrackerNav };

apps.register({
  id: 'turn-tracker',
  name: 'Turn Tracker',
  icon: '▦',
  blurb: 'The turn grid, the staff roster, and the day’s book when a scheduling plugin is on.',
  page: TurnTracker,
  header: TurnTrackerNav
});
